import { isRoleLabel, looksLikeSpeakerName, sameSpeakerName, speakerKey } from "@/lib/transcript/speaker-names";

/**
 * One transcript line: does it name a speaker, and what did they say? These are
 * the only line rules for speaker labels. `speaker-turns.js` applies them to
 * whole transcripts; nothing else should split "Name: text" itself.
 */

// Elapsed call time: m:ss, mm:ss or h:mm:ss, with optional fractional seconds...
const TIME = String.raw`\d{1,3}:[0-5]\d(?::[0-5]\d)?(?:[.,]\d+)?`;
// ...or in units, as some call exports print it: "14s", "1min 2s", "1h 5min".
const UNIT_TIME = String.raw`(?:\d{1,2}h ?)?(?:\d{1,3}min ?)?\d{1,2}s|(?:\d{1,2}h ?)?\d{1,3}min`;
// Enclosed ("[0:14]", "(0:14)") or bare. A bare time may not touch letters,
// digits or another time, so "1:00:25" reads as one timestamp.
const TIMESTAMP = new RegExp(
  String.raw`\[${TIME}\]|\(${TIME}\)|(?<![\p{L}\p{N}:.,])${TIME}(?!\p{N}|:\d)|(?<![\p{L}\p{N}])(?:${UNIT_TIME})(?![\p{L}\p{N}])`,
  "gu",
);
// What a timestamp that opens a turn is joined to the name by: "14s - Dana",
// "0:14 | Dana". A bare time followed by a plain space anchors nothing.
const JOINER = /^\s*([-–—|]?)\s*/;
// A name printed beside a timestamp, or on its own line above one, comes as it
// was typed: "dan d", "Marcus (Ops)".
const TYPED_NAME = /^\p{L}[\p{L}\p{M}\p{N}\s.'’()&/+-]{0,59}$/u;
const MAX_TYPED_NAME_WORDS = 6;
const MAX_LABEL_SCAN = 80;
// The words before a colon that could be a mid-line label.
const LABEL_BEFORE_COLON = /([\p{L}][\p{L}\p{N}.'’&()/-]*(?:\s+[\p{L}\p{N}&+/(-][\p{L}\p{N}.'’&()/-]*){0,5})$/u;

function timestampSpans(text) {
  return [...text.matchAll(TIMESTAMP)].map((match) => ({
    start: match.index,
    end: match.index + match[0].length,
    enclosed: match[0][0] === "[" || match[0][0] === "(",
  }));
}

// Colons that delimit something, not the ones inside a timestamp.
function colonsOutside(text, spans) {
  const colons = [];
  for (let index = text.indexOf(":"); index !== -1; index = text.indexOf(":", index + 1)) {
    if (!spans.some((span) => index >= span.start && index < span.end)) colons.push(index);
  }
  return colons;
}

// Emphasis a notetaker wraps a name in (**Name**, __Name__) is not part of it,
// but a lone "_" can be, as in a legacy "Speaker _" label.
const EMPHASIS = /(\*{1,2}|_{1,2})(?=\S)(.+?)(?<=\S)\1/g;
const cleanLabel = (text) => text.replace(EMPHASIS, "$2").replace(/\s+/g, " ").trim();
const isMarkup = (text) => /^[\s*_]*$/.test(text);

/** A line that is nothing but a timestamp: "0:04", "[1:02:03]", "(0:08)". */
export function isStandaloneTimestamp(line) {
  const text = String(line ?? "").trim();
  const spans = timestampSpans(text);
  return spans.length === 1 && spans[0].start === 0 && spans[0].end === text.length;
}

/** A name as typed beside a timestamp or on its own line above one. */
export function looksLikeTypedName(label) {
  return TYPED_NAME.test(label) && label.split(/\s+/).length <= MAX_TYPED_NAME_WORDS;
}

/**
 * Timestamps joined to what follows by a dash or pipe inside what a speaker
 * said: "... speaking. 15s - Dana Hi there". A transcript whose line breaks
 * were lost has one before every turn, inside one block of text. An enclosed
 * timestamp is left out: "[0:20]" inside a turn marks time in intact prose too.
 *
 * @param {string} text
 */
export function countJoinedTimestamps(text) {
  const value = String(text ?? "");
  return timestampSpans(value).filter((span) =>
    !span.enclosed && Boolean(value.slice(span.end).match(JOINER)[1])).length;
}

function isHeaderName(label, { anchored, knownSpeakers }) {
  if (!label || label.length > MAX_LABEL_SCAN) return false;
  if (knownSpeakers.some((name) => sameSpeakerName(label, name))) return true;
  if (/[[\]:]/.test(label)) return false;
  if (isRoleLabel(label)) return true;
  return anchored ? looksLikeTypedName(label) : looksLikeSpeakerName(label);
}

// "Dana Reyes Hello there" with Dana Reyes in the cast: the longest run of
// opening words that is a known speaker, and the rest as what they said. The
// speaker comes back in the cast's spelling, so a comma after the name is not
// part of it.
function splitKnownSpeaker(text, knownSpeakers) {
  const words = [...text.matchAll(/\S+/g)].slice(0, MAX_TYPED_NAME_WORDS);
  for (let count = words.length; count > 0; count -= 1) {
    const end = words[count - 1].index + words[count - 1][0].length;
    const key = speakerKey(text.slice(0, end));
    const speaker = knownSpeakers.find((name) => speakerKey(name) === key);
    if (speaker) return { speaker, end, content: text.slice(end).replace(/^[\s,;:]+/, "").trim() };
  }
  return null;
}

/**
 * Split a speaker header: a name with an optional timestamp before or after it,
 * then a colon and what they said, or a timestamp and the words on the lines
 * below.
 *
 *   Dana: Hello            [0:14] Dana: Hello        **Dana** (0:14): Hello
 *   Dana [0:14]: Hello     Dana | 0:14               Dana 0:14
 *   14s - Dana             1min 2s - Dana: Hello     0:14 Dana: Hello
 *
 * The delimiter is the first colon outside a timestamp, so a time never splits
 * the line. A name beside an enclosed or piped timestamp may be typed any way;
 * any other label must look like a name, or prose such as "For example:" would
 * open a turn. After an opening timestamp, a known speaker's name may run
 * straight into their words ("14s - Dana Reyes Hello there").
 *
 * @param {unknown} line
 * @param {Object} [options]
 * @param {string[]} [options.knownSpeakers] Names accepted whatever their shape.
 * @param {boolean} [options.typedNames] Accept a name as typed, for formats that
 *   mark the speaker themselves, such as a caption cue.
 * @returns {{ speaker: string, content: string, timestamped: boolean }|null}
 */
export function splitSpeakerLine(line, { knownSpeakers = [], typedNames = false } = {}) {
  if (typeof line !== "string") return null;
  const text = line.trim();
  const spans = timestampSpans(text);
  const [colon = -1] = colonsOutside(text, spans);
  const lead = spans[0] && isMarkup(text.slice(0, spans[0].start)) ? spans[0] : null;
  const joiner = lead ? text.slice(lead.end).match(JOINER) : null;
  const labelStart = lead ? lead.end + joiner[0].length : 0;
  if (lead && knownSpeakers.length) {
    const known = splitKnownSpeaker(text.slice(labelStart), knownSpeakers);
    if (known) return { speaker: known.speaker, content: known.content, timestamped: true };
  }
  const headEnd = colon === -1 ? text.length : colon;
  const trail = spans.find((span) => span.start >= labelStart && span.end <= headEnd);
  // "14s - Dana" alone on a line heads the words below it; "[0:14] Hello there"
  // is a timestamped line of prose.
  if (colon === -1 && !trail && !joiner?.[1]) return null;

  let label = text.slice(labelStart, trail ? trail.start : headEnd);
  let content = colon === -1 ? "" : text.slice(colon + 1);
  let piped = joiner?.[1] === "|";
  if (trail) {
    const afterTrail = text.slice(trail.end, headEnd);
    // "Dana 0:14: Hello" is a header; "Call at 10:30 today: done" is prose.
    if (colon !== -1 && !isMarkup(afterTrail)) return null;
    if (colon === -1) content = afterTrail;
    piped = /\|\s*$/.test(label);
    label = label.replace(/\|\s*$/, "");
  }
  label = cleanLabel(label);
  // A dash after a bare time anchors nothing: "10:30 - pricing: agreed" is an
  // agenda line, so that label must still look like a name.
  const anchored = Boolean(typedNames || lead?.enclosed || trail?.enclosed || piped);
  if (!isHeaderName(label, { anchored, knownSpeakers })) return null;
  return { speaker: label, content: content.trim(), timestamped: Boolean(lead || trail) };
}

// The name after a timestamp anchor inside a line. With a cast, the longest
// opening run of words that is a known speaker; without one, the words up to a
// colon within reach, when they are shaped like a name.
function readAnchoredName(text, start, knownSpeakers) {
  const rest = text.slice(start, start + MAX_LABEL_SCAN);
  if (knownSpeakers.length) {
    const known = splitKnownSpeaker(rest, knownSpeakers);
    return known ? { speaker: known.speaker, contentStart: start + known.end } : null;
  }
  const [colon] = colonsOutside(rest, timestampSpans(rest));
  if (colon === undefined) return null;
  const label = cleanLabel(rest.slice(0, colon));
  if (!label || timestampSpans(label).length || !looksLikeSpeakerName(label)) return null;
  return { speaker: label, contentStart: start + colon + 1 };
}

/**
 * Speaker labels inside a line, where a transcript lost its line breaks:
 * "Customer: Hello Denise Rucker-Burton: My name is...". Mid-line, one word
 * before a colon ("ask Kai: why now?") is prose far more often than a speaker,
 * so a label takes two name words or one of our role labels as our writers
 * capitalise it ("Customer:", never "the customer:"). A timestamp anchor
 * ("... speaking. 15s - Dana Reyes Hi there") also opens a label, read by
 * `readAnchoredName`.
 *
 * @param {string} text
 * @param {Object} [options]
 * @param {boolean} [options.fromStart] Whether a label may open `text`: true
 *   when `text` follows a header on the same line.
 * @param {string[]} [options.knownSpeakers] The cast, when the caller knows it.
 * @returns {Array<{ speaker: string, start: number, contentStart: number }>}
 */
export function findRunOnLabels(text, { fromStart = false, knownSpeakers = [] } = {}) {
  const spans = timestampSpans(text);
  const labels = [];
  for (const span of spans) {
    if (span.start === 0 && !fromStart) continue;
    const joiner = text.slice(span.end).match(JOINER);
    if (!span.enclosed && !joiner[1]) continue;
    const name = readAnchoredName(text, span.end + joiner[0].length, knownSpeakers);
    if (name) labels.push({ speaker: name.speaker, start: span.start, contentStart: name.contentStart });
  }
  for (const colon of colonsOutside(text, spans)) {
    // The anchor already covers a colon label such as "[0:14] Dana Reyes:".
    if (labels.some((label) => label.contentStart === colon + 1)) continue;
    const previous = Math.max(0, ...labels.map((label) => label.contentStart).filter((at) => at <= colon));
    const scanStart = Math.max(previous, colon - MAX_LABEL_SCAN);
    const token = text.slice(scanStart, colon).match(LABEL_BEFORE_COLON);
    if (!token) continue;
    const words = [...token[1].matchAll(/\S+/g)];
    // Shortest name first, so "Hello Denise Rucker-Burton" yields the name alone.
    for (let index = words.length - 1; index >= 0; index -= 1) {
      const start = scanStart + token.index + words[index].index;
      if (start === 0 && !fromStart) break;
      const label = token[1].slice(words[index].index);
      const roleLabel = isRoleLabel(label) && /^\p{Lu}/u.test(label);
      if (roleLabel || (words.length - index >= 2 && looksLikeSpeakerName(label))) {
        labels.push({ speaker: label, start, contentStart: colon + 1 });
        break;
      }
    }
  }
  return labels.sort((a, b) => a.start - b.start);
}
